// The always-dark account key panel of the Devices page and its parts (design/isomorphic/src/
// pages/device-cards.tsx): a glow-lit frame, the fingerprint as a grid of chips, the facts, the
// warning about losing every device and the recovery file, and inputs and buttons that read
// on the dark surface.
import { Keyhole } from "@phosphor-icons/react";
import { Fragment, type InputHTMLAttributes, type ReactNode } from "react";
import { Button, type ButtonProps } from "rizzui";

import { useT } from "../i18n";
import { cn } from "../ui/cn";

export function AccountPanel({ subtitle, children, className }: { subtitle?: string; children: ReactNode; className?: string }) {
  const t = useT().account.panel;
  return (
    <section aria-label={t.title} className={cn("relative overflow-hidden rounded-xl bg-panel p-6 text-panel-foreground", className)}>
      <div className="pointer-events-none absolute end-0 top-0 h-56 w-56 rounded-full bg-primary/30 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 start-0 h-48 w-48 rounded-full bg-secondary/20 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-secondary">
            <Keyhole weight="duotone" className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h3 className="text-base !text-white">{t.title}</h3>
            <div className="text-xs text-panel-foreground/70">{subtitle ?? t.subtitle}</div>
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

export function PanelFingerprint({ value, testId }: { value: string; testId?: string }) {
  const t = useT().account.panel;
  return (
    <>
      <div className="mt-6 text-[11px] uppercase tracking-[0.16em] text-panel-foreground/60">{t.fingerprint}</div>
      <div data-testid={testId} className="mt-2 grid grid-cols-4 gap-2">
        {value.split(" ").map((g, i) => (
          <Fragment key={i}>
            {i > 0 ? " " : null}
            <span className="rounded-lg bg-white/[0.07] py-2 text-center font-mono text-[14px] font-semibold tracking-wider text-white">{g}</span>
          </Fragment>
        ))}
      </div>
    </>
  );
}

export function PanelFacts({ facts }: { facts: [string, ReactNode][] }) {
  return (
    <dl className="mt-6 grid grid-cols-2 gap-4 text-[13px]">
      {facts.map(([label, value]) => (
        <div key={label}>
          <dt className="text-panel-foreground/60">{label}</dt>
          <dd className="mt-0.5 font-medium text-white">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function PanelNote({ children }: { children: ReactNode }) {
  return <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.04] p-3.5 text-[12.5px] leading-relaxed">{children}</div>;
}

export function LossWarning() {
  const t = useT().account.panel;
  return <PanelNote>{t.lossWarning(<span className="font-semibold text-white">{t.lossWarningAnd}</span>)}</PanelNote>;
}

export function PanelInput({ label, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block min-w-0 text-[13px]">
      <span className="text-panel-foreground/70">{label}</span>
      <input
        {...props}
        className="mt-1 block w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-secondary focus:ring-0 file:me-3 file:rounded-md file:border-0 file:bg-white/10 file:px-2.5 file:py-1 file:text-white"
      />
    </label>
  );
}

/** The panel's main action, white on the dark surface. */
export function PanelPrimary({ className, ...props }: ButtonProps) {
  return <Button {...props} className={cn("gap-1.5 bg-white text-gray-900 hover:bg-white/90 dark:bg-white dark:text-[#0d0d1a]", className)} />;
}

export function PanelSecondary({ className, ...props }: ButtonProps) {
  return <Button variant="outline" {...props} className={cn("border-white/20 text-white hover:border-white/40 hover:bg-white/5", className)} />;
}
